export const inputTypes = ['text', 'password'];
const MASK_SYMBOL_LETTER = 'A';
const MASK_SYMBOL_DIGIT = '0';
const EMPTY_CHAR = ' ';
const MASK_MODE_DIGIT = 0;
const MASK_MODE_LETTER = 1;
const MASK_MODE_EXACT = 2;
const MASK_PLACEHOLDER_DELIMITER = '@@';
const MASK_PLACEHOLDER = '_';
const testMaskedValue = (maskSymbol, inputChar) => {
  switch (maskSymbol.mode) {
    case MASK_MODE_DIGIT:
      {
        return /\d/.test(inputChar);
      }
    case MASK_MODE_LETTER:
      {
        return /\p{Letter}/u.test(inputChar);
      }
  }
  return false;
};
const isExactMode = maskSymbol => maskSymbol.mode === MASK_MODE_EXACT;
const isEmptyChar = char => char === EMPTY_CHAR;
export const getMaskSymbols = maskValue => {
  const res = [];
  const [mask, placeholder] = maskValue.split(MASK_PLACEHOLDER_DELIMITER);
  const maskSymbols = [...mask];
  const placeholderChars = placeholder != null ? [...placeholder] : [];
  for (let maskIndex = 0, placeholderIndex = 0; maskIndex < maskSymbols.length; maskIndex++, placeholderIndex++) {
    if (maskSymbols[maskIndex] === '\\') {
      maskIndex += 1;
      res.push({
        value: maskSymbols[maskIndex],
        mode: MASK_MODE_EXACT,
        placeholder: placeholderChars[placeholderIndex] ?? maskSymbols[maskIndex]
      });
      continue;
    }
    if (maskSymbols[maskIndex] === MASK_SYMBOL_LETTER || maskSymbols[maskIndex] === MASK_SYMBOL_DIGIT) {
      res.push({
        value: maskSymbols[maskIndex],
        mode: maskSymbols[maskIndex] === MASK_SYMBOL_LETTER ? MASK_MODE_LETTER : MASK_MODE_DIGIT,
        placeholder: placeholderChars[placeholderIndex] ?? MASK_PLACEHOLDER
      });
      continue;
    }
    res.push({
      value: maskSymbols[maskIndex],
      mode: MASK_MODE_EXACT,
      placeholder: placeholderChars[placeholderIndex] ?? maskSymbols[maskIndex]
    });
  }
  return res;
};
const clearCharsSelection = (chars, selectionStart, selectionEnd) => {
  for (let i = selectionStart; i < selectionEnd && i < chars.length; i++) {
    chars[i] = EMPTY_CHAR;
  }
};
const getPlaceholder = (chars, maskSymbols) => {
  const res = new Array(maskSymbols.length);
  for (let i = 0; i < maskSymbols.length; i++) {
    res[i] = i >= chars.length || isEmptyChar(chars[i]) || isExactMode(maskSymbols[i]) ? maskSymbols[i].placeholder : EMPTY_CHAR;
  }
  return res.join('');
};
const isMaskedInputComplete = (chars, maskSymbols) => {
  if (chars.length > maskSymbols.length) {
    throw new Error('chars.length > maskSymbols.length');
  }
  if (chars.length < maskSymbols.length) {
    return false;
  }
  for (let i = 0; i < maskSymbols.length; i++) {
    if (i >= chars.length || !isExactMode(maskSymbols[i]) && !testMaskedValue(maskSymbols[i], chars[i])) {
      return false;
    }
  }
  return true;
};
const getCharsFromInputValue = (inputValue, maskSymbols) => {
  const chars = new Array(maskSymbols.length);
  let i = 0;
  for (const c of inputValue) {
    chars[i++] = c;
  }
  chars.fill(EMPTY_CHAR, i);
  return chars;
};
const isCursorAtTheBeginning = (maskSymbols, selectoinStart) => {
  if (selectoinStart >= maskSymbols.length) {
    return false;
  }
  for (let i = 0; i < selectoinStart; i++) {
    if (!isExactMode(maskSymbols[i])) {
      return false;
    }
  }
  return true;
};
const getMergedValue = (chars, maskSymbols) => {
  if (!isMaskedInputComplete(chars, maskSymbols)) {
    return '';
  }
  const res = new Array(chars.length);
  for (let i = 0; i < chars.length; i++) {
    res[i] = isExactMode(maskSymbols[i]) ? maskSymbols[i].value : chars[i];
  }
  return res.join('');
};
const findLastNonEmptyCharIndex = chars => {
  for (let i = chars.length - 1; i >= 0; i--) {
    if (!isEmptyChar(chars[i])) {
      return i;
    }
  }
  return -1;
};
const compileResult = (chars, maskSymbols, cursorPos) => {
  if (chars.length > maskSymbols.length) {
    chars.length = maskSymbols.length;
  }
  let lastEmptyPos = findLastNonEmptyCharIndex(chars) + 1;
  while (lastEmptyPos < maskSymbols.length && isExactMode(maskSymbols[lastEmptyPos])) {
    ++lastEmptyPos;
  }
  if (lastEmptyPos < chars.length) {
    chars.length = lastEmptyPos;
  }
  return {
    value: chars.join(''),
    placeholder: getPlaceholder(chars, maskSymbols),
    cursorPos: Math.min(cursorPos, chars.length),
    mergedValue: getMergedValue(chars, maskSymbols)
  };
};
export const deleteContentBackward = (inputValue, maskSymbols, selectionStart, selectionEnd) => {
  if (selectionEnd === 0) {
    return null;
  }
  const chars = getCharsFromInputValue(inputValue, maskSymbols);
  if (selectionStart !== selectionEnd) {
    clearCharsSelection(chars, selectionStart, selectionEnd);
    return compileResult(chars, maskSymbols, selectionStart);
  }
  let cusrsorPos = selectionStart;
  while (cusrsorPos > 0 && isExactMode(maskSymbols[cusrsorPos - 1])) {
    --cusrsorPos;
  }
  if (cusrsorPos > 0) {
    clearCharsSelection(chars, cusrsorPos - 1, cusrsorPos);
    do {
      --cusrsorPos;
    } while (cusrsorPos > 0 && isExactMode(maskSymbols[cusrsorPos - 1]));
  }
  return compileResult(chars, maskSymbols, cusrsorPos);
};
export const deleteContentForward = (inputValue, maskSymbols, selectionStart, selectionEnd) => {
  if (selectionStart >= maskSymbols.length) {
    return null;
  }
  const chars = getCharsFromInputValue(inputValue, maskSymbols);
  if (selectionStart !== selectionEnd) {
    clearCharsSelection(chars, selectionStart, selectionEnd);
    return compileResult(chars, maskSymbols, selectionEnd);
  }
  let cursorPos = selectionStart;
  while (cursorPos < maskSymbols.length && isExactMode(maskSymbols[cursorPos])) {
    cursorPos++;
  }
  if (cursorPos < chars.length) {
    clearCharsSelection(chars, cursorPos, cursorPos + 1);
  }
  return compileResult(chars, maskSymbols, cursorPos + 1);
};
export const beginMaskedComposition = (inputValue, maskSymbols, selectionStart) => {
  const chars = getCharsFromInputValue(inputValue, maskSymbols);
  const placeholder = new Array(chars.length);
  for (let i = 0; i < maskSymbols.length; i++) {
    placeholder[i] = isEmptyChar(chars[i]) || isExactMode(maskSymbols[i]) ? maskSymbols[i].placeholder : EMPTY_CHAR;
  }
  placeholder[selectionStart] = EMPTY_CHAR;
  chars.splice(selectionStart, 1);
  return {
    value: chars.join(''),
    placeholder: placeholder.join('')
  };
};
export const endMaskedComposition = (inputValue, data, maskSymbols, selectionStart, wasValueInserted) => {
  let cursorPos = selectionStart;
  const chars = getCharsFromInputValue(inputValue, maskSymbols);
  const dataChars = [...data];
  if (!wasValueInserted) {
    chars.splice(cursorPos, 0, ...dataChars);
    chars.length = maskSymbols.length;
    cursorPos += dataChars.length;
  }
  cursorPos -= dataChars.length;
  clearCharsSelection(chars, cursorPos, cursorPos + dataChars.length);
  while (cursorPos < maskSymbols.length && isExactMode(maskSymbols[cursorPos])) {
    cursorPos++;
  }
  if (cursorPos >= maskSymbols.length) {
    return compileResult(chars, maskSymbols, cursorPos);
  }
  for (let dataPos = 0; dataPos < dataChars.length && cursorPos < maskSymbols.length; ++dataPos, ++cursorPos) {
    const data = dataChars[dataPos];
    if (isEmptyChar(chars[cursorPos]) && testMaskedValue(maskSymbols[cursorPos], data)) {
      chars[cursorPos] = data;
    }
  }
  while (cursorPos < maskSymbols.length && isExactMode(maskSymbols[cursorPos])) {
    cursorPos++;
  }
  return compileResult(chars, maskSymbols, cursorPos);
};
export const insertText = (inputValue, data, maskSymbols, selectionStart, selectionEnd) => {
  const chars = getCharsFromInputValue(inputValue, maskSymbols);
  let cursorPos = selectionStart;
  clearCharsSelection(chars, selectionStart, selectionEnd);
  while (cursorPos < maskSymbols.length && isExactMode(maskSymbols[cursorPos])) {
    cursorPos++;
  }
  if (cursorPos >= maskSymbols.length) {
    return compileResult(chars, maskSymbols, cursorPos);
  }
  if (testMaskedValue(maskSymbols[cursorPos], data)) {
    if (cursorPos >= chars.length) {
      chars.length = cursorPos + 1;
    }
    chars[cursorPos] = data;
    do {
      cursorPos++;
    } while (cursorPos < maskSymbols.length && isExactMode(maskSymbols[cursorPos]));
  }
  return compileResult(chars, maskSymbols, cursorPos);
};
export const insertFromPaste = (inputValue, data, maskSymbols, selectionStart, selectionEnd) => {
  const chars = getCharsFromInputValue(inputValue, maskSymbols);
  let cursorPos = selectionStart;
  clearCharsSelection(chars, selectionStart, selectionEnd);
  if (isCursorAtTheBeginning(maskSymbols, cursorPos)) {
    cursorPos = 0;
  } else {
    while (cursorPos < maskSymbols.length && isExactMode(maskSymbols[cursorPos])) {
      cursorPos++;
    }
  }
  if (cursorPos >= maskSymbols.length) {
    return compileResult(chars, maskSymbols, cursorPos);
  }
  const dataChars = [...data];
  for (let dataPos = 0; dataPos < dataChars.length && cursorPos < maskSymbols.length;) {
    const data = dataChars[dataPos];
    const maskSymbol = maskSymbols[cursorPos];
    if (isExactMode(maskSymbol)) {
      if (maskSymbol.value === data) {
        ++dataPos;
      }
      ++cursorPos;
    } else {
      if (testMaskedValue(maskSymbol, data)) {
        chars[cursorPos] = data;
        ++cursorPos;
      }
      ++dataPos;
    }
  }
  return compileResult(chars, maskSymbols, cursorPos);
};
export const splitValueAndMask = (inputValue, maskSymbols) => {
  const chars = getCharsFromInputValue('', maskSymbols);
  const dataChars = getCharsFromInputValue(inputValue, maskSymbols);
  let cursorPos = 0;
  for (let dataPos = 0; dataPos < dataChars.length && cursorPos < maskSymbols.length;) {
    const data = dataChars[dataPos];
    const maskSymbol = maskSymbols[cursorPos];
    if (isExactMode(maskSymbol)) {
      if (maskSymbol.value === data) {
        ++dataPos;
      }
      ++cursorPos;
    } else {
      if (testMaskedValue(maskSymbol, data)) {
        chars[cursorPos] = data;
        ++cursorPos;
      }
      ++dataPos;
    }
  }
  return compileResult(chars, maskSymbols, chars.length);
};
export const getMergedValueSliced = (inputValue, maskSymbols, selectionStart, selectionEnd) => {
  const chars = getCharsFromInputValue(inputValue, maskSymbols);
  for (let i = selectionStart; i < selectionEnd && i < maskSymbols.length; i++) {
    if (isExactMode(maskSymbols[i])) {
      chars[i] = maskSymbols[i].value;
    }
  }
  return chars.slice(selectionStart, selectionEnd).join('');
};